/**
 * Admin shell sections (numeric codes). Align with plan: 101 users, 102 sectors, …
 * Query sync: ?admin=101
 */
export type AdminSectionNonLanding =
    | '101'
    | '102'
    | '103'
    | '104'
    | '108'
    | '110'
    | '110.1'
    | '110.2'
    | '110.3'
    | '110.4'
    | '111'
    | '112'
    | '113'
    | '1211'
    | '1212'
    | '1213'
    | '122'
    | '123'
    | '124'
    | '125'
    | '126'
    | '127'
    | '128'
    | '129'
    | '133'
    | '132'
    | '140'
    | '142'
    | '150'
    | '160'
    | '170'
    | '172'
    | '180'
    | '190';

export type AdminSectionId = 'landing' | AdminSectionNonLanding;

/** Grupiranje sekcija u 4 teme za pregledniji landing modula 100. */
export type AdminThemeId = 'people' | 'data' | 'finance' | 'system';

export type AdminThemeDefinition = {
    id: AdminThemeId;
    labelHr: string;
    labelEn: string;
    descriptionHr: string;
    descriptionEn: string;
    icon: string;
};

export const ADMIN_THEMES: readonly AdminThemeDefinition[] = [
    { id: 'people', labelHr: 'Ljudi i pristup', labelEn: 'People & access', descriptionHr: 'Korisnici, uloge, sektori, odjeli, zaposlenici.', descriptionEn: 'Users, roles, sectors, departments, employees.', icon: '👥' },
    { id: 'data', labelHr: 'Šifrarnici', labelEn: 'Master data', descriptionHr: 'Proizvodi, partneri, projekti, šifre narudžbe.', descriptionEn: 'Products, partners, projects, order codes.', icon: '🏷️' },
    { id: 'finance', labelHr: 'Financije', labelEn: 'Finance', descriptionHr: 'URA, IRA, virmani, banke, leasing, pozajmice.', descriptionEn: 'AP, AR, payments, banks, leasing, loans.', icon: '💰' },
    { id: 'system', labelHr: 'Sustav', labelEn: 'System', descriptionHr: 'Integracije, zaključavanja, audit log.', descriptionEn: 'Integrations, period locks, audit log.', icon: '⚙️' },
];

export type AdminSectionDefinition = {
    id: AdminSectionNonLanding;
    code: number;
    labelHr: string;
    labelEn: string;
    requiresSuperadmin: boolean;
    requiresStructureAccess: boolean;
    /** Tema u koju ova sekcija pripada za grupirani landing modula 100. */
    theme: AdminThemeId;
    /** Owner-only (samo vlasnik vjakomin), i kad je korisnik superadministrator. */
    ownerOnly?: boolean;
    /** Ako je postavljeno, sekcija je pod-modul roditelja — ne pojavljuje se kao zasebna
     *  kartica u Administracija landingu i nasljeđuje granul (m.<parentCode>) od roditelja. */
    parentSectionId?: AdminSectionNonLanding;
};

/** Ordered by code for landing grid */
export const ADMIN_SECTION_DEFINITIONS: readonly AdminSectionDefinition[] = [
    { id: '101', code: 101, labelHr: 'Korisnici', labelEn: 'Users', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'people' },
    { id: '104', code: 104, labelHr: 'Dodjele korisnika', labelEn: 'User assignments', requiresSuperadmin: true, requiresStructureAccess: false, parentSectionId: '101', theme: 'people' },
    { id: '108', code: 108, labelHr: 'Moduli po tvrtki', labelEn: 'Modules per company', requiresSuperadmin: false, requiresStructureAccess: true, theme: 'people' },
    { id: '102', code: 102, labelHr: 'Sektori', labelEn: 'Sectors', requiresSuperadmin: false, requiresStructureAccess: true, theme: 'people' },
    { id: '103', code: 103, labelHr: 'Odjeli', labelEn: 'Departments', requiresSuperadmin: false, requiresStructureAccess: true, theme: 'people' },
    { id: '126', code: 126, labelHr: 'Zaposlenici / osobe zaduženja', labelEn: 'Employees / responsible persons', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'people' },

    { id: '110', code: 110, labelHr: 'Proizvodi', labelEn: 'Products', requiresSuperadmin: false, requiresStructureAccess: true, theme: 'data' },
    { id: '110.1', code: 110.1, labelHr: 'Tipovi proizvoda', labelEn: 'Product types', requiresSuperadmin: false, requiresStructureAccess: true, parentSectionId: '110', theme: 'data' },
    { id: '110.2', code: 110.2, labelHr: 'Rokovi isporuke', labelEn: 'Delivery types', requiresSuperadmin: false, requiresStructureAccess: true, parentSectionId: '110', theme: 'data' },
    { id: '110.3', code: 110.3, labelHr: 'Kategorije proizvoda', labelEn: 'Product categories', requiresSuperadmin: false, requiresStructureAccess: true, parentSectionId: '110', theme: 'data' },
    { id: '110.4', code: 110.4, labelHr: 'Aktivacija (is_active)', labelEn: 'Activation (is_active)', requiresSuperadmin: false, requiresStructureAccess: false, parentSectionId: '110', theme: 'data' },
    { id: '111', code: 111, labelHr: 'Partneri', labelEn: 'Partners', requiresSuperadmin: false, requiresStructureAccess: true, theme: 'data' },
    { id: '112', code: 112, labelHr: 'Partneri (dobavljači)', labelEn: 'Partners (suppliers)', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'data' },
    { id: '113', code: 113, labelHr: 'Mapping partnera (URA / IRA)', labelEn: 'Partner mapping (AP / AR)', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'data' },
    { id: '125', code: 125, labelHr: 'Šifra narudžbe', labelEn: 'Purchase order code', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'data' },
    { id: '127', code: 127, labelHr: 'Projekti', labelEn: 'Projects', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'data' },
    { id: '128', code: 128, labelHr: 'Skladišta zlata', labelEn: 'Gold warehouses', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'data' },
    { id: '129', code: 129, labelHr: 'Dobavljači zlata', labelEn: 'Gold suppliers', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'data' },
    { id: '133', code: 133, labelHr: 'Kategorije po modulu (40)', labelEn: 'Categories per module (40)', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'data' },

    { id: '1211', code: 1211, labelHr: 'URA — ulazni računi', labelEn: 'APA — incoming invoices', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'finance' },
    { id: '1212', code: 1212, labelHr: 'IRA — izlazni računi', labelEn: 'ARA — outgoing invoices', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'finance' },
    { id: '1213', code: 1213, labelHr: 'Virmani', labelEn: 'Standing payments', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'finance' },
    { id: '122', code: 122, labelHr: 'Banke (admin)', labelEn: 'Banks (admin)', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'finance' },
    { id: '132', code: 132, labelHr: 'Bankovni računi i podračuni', labelEn: 'Bank accounts & sub-accounts', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'finance' },
    { id: '123', code: 123, labelHr: 'Leasing (admin)', labelEn: 'Leasing (admin)', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'finance' },
    { id: '124', code: 124, labelHr: 'Pozajmice — knjiga', labelEn: 'Loans book', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'finance' },
    { id: '128', code: 128, labelHr: 'Garancije i manual pozicije', labelEn: 'Guarantees & manual positions', requiresSuperadmin: false, requiresStructureAccess: false, theme: 'finance' },

    { id: '140', code: 140, labelHr: 'DocDoc integracija', labelEn: 'DocDoc integration', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'system' },
    { id: '142', code: 142, labelHr: 'Centralni ingest (obrada@) + Praćenje importa', labelEn: 'Central ingest + Import audit', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'system' },
    { id: '150', code: 150, labelHr: 'Zaključavanje godina', labelEn: 'Period locks', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'system' },
    { id: '160', code: 160, labelHr: 'Audit log', labelEn: 'Audit log', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'system' },
    { id: '170', code: 170, labelHr: 'Bilješke modula (log izmjena)', labelEn: 'Module notes (change log)', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'system' },
    // m.172 je pod-modul m.142 (Audit tab) — ne pojavljuje se kao zasebna kartica.
    { id: '172', code: 172, labelHr: 'Praćenje importa', labelEn: 'Import audit', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'system', parentSectionId: '142' },
    { id: '180', code: 180, labelHr: 'Obavijesti — primatelji', labelEn: 'Notifications — recipients', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'system', ownerOnly: true },
    { id: '190', code: 190, labelHr: 'Aktivnost korisnika (BIS + Sandbox)', labelEn: 'User activity (BIS + Sandbox)', requiresSuperadmin: true, requiresStructureAccess: false, theme: 'system' },
];

/**
 * Sekcije unutar zadane teme, filtrirane prema useru. Pod-sekcije (parentSectionId)
 * se izostavljaju — pristupaju se kroz roditeljsku karticu.
 */
export function themeSectionsForUser(user: { role: string; allowed_modules?: string[] }, theme: AdminThemeId): readonly AdminSectionDefinition[] {
    return ADMIN_SECTION_DEFINITIONS
        .filter((def) => def.theme === theme && ! def.parentSectionId)
        .filter((def) => userMayAccessAdminSection(user, def.id));
}

/** Teme koje user može vidjeti (barem jedna sekcija u temi je dostupna). */
export function visibleThemesForUser(user: { role: string; allowed_modules?: string[] }): readonly AdminThemeDefinition[] {
    return ADMIN_THEMES.filter((th) => themeSectionsForUser(user, th.id).length > 0);
}

export function adminSectionNavLabel(def: AdminSectionDefinition, locale: 'hr' | 'en'): string {
    const title = locale === 'en' ? def.labelEn : def.labelHr;

    return `${def.code} · ${title}`;
}

export function adminSectionDefinition(id: AdminSectionNonLanding): AdminSectionDefinition | undefined {
    return ADMIN_SECTION_DEFINITIONS.find((row) => row.id === id);
}

function hasFullCoarseAdmin(a: string[] | undefined): boolean {
    return Boolean(a?.includes('admin'));
}

function hasGranuleForSection(a: string[] | undefined, sectionId: AdminSectionNonLanding): boolean {
    if (a?.includes(`m.${sectionId}`)) {
        return true;
    }

    // Nasljedi granul od roditelja (npr. m.110 daje pristup 110.1/110.2/110.3).
    const def = adminSectionDefinition(sectionId);
    if (def?.parentSectionId) {
        const parentCode = adminSectionDefinition(def.parentSectionId)?.code;
        if (parentCode != null && a?.includes(`m.${parentCode}`)) {
            return true;
        }
    }

    if (sectionId === '1211' || sectionId === '1212' || sectionId === '1213') {
        return Boolean(a?.includes('m.121'));
    }

    if (sectionId === '125') {
        return Boolean(a?.includes('m.125') || a?.includes('m.121'));
    }

    if (sectionId === '112') {
        return Boolean(a?.includes('m.112') || a?.includes('m.121'));
    }

    if (sectionId === '113') {
        return Boolean(a?.includes('m.113') || a?.includes('m.121'));
    }

    if (sectionId === '126' || sectionId === '127') {
        return Boolean(a?.includes(`m.${sectionId}`) || a?.includes('m.125'));
    }

    if (sectionId === '122' || sectionId === '123' || sectionId === '124') {
        return Boolean(a?.includes(`m.${sectionId}`));
    }

    return false;
}

/** Granular / coarse ACL for admin UI (Phase C). Superadmin: full access. */
export function userMayAccessAdminSection(user: { role: string; allowed_modules?: string[]; email?: string | null }, section: AdminSectionId): boolean {
    // Owner-only sekcije (npr. 180 Obavijesti) — samo vlasnik, čak i kad je korisnik superadministrator.
    if (section !== 'landing') {
        const od = adminSectionDefinition(section);
        if (od?.ownerOnly && String(user.email ?? '').toLowerCase() !== 'vjakomin@gmail.com') {
            return false;
        }
    }
    if (user.role === 'superadministrator') {
        return true;
    }

    const isAdministrator = user.role === 'administrator';
    const a = user.allowed_modules;
    const loose = !a || a.length === 0;

    if (section === 'landing') {
        // Administratori s praznim allowed_modules vide sve (legacy "loose" pristup).
        if (isAdministrator && loose) {
            return true;
        }
        if (hasFullCoarseAdmin(a)) {
            return true;
        }
        if (a?.includes('m.100')) {
            return true;
        }

        return ADMIN_SECTION_DEFINITIONS.some((def) => hasGranuleForSection(a, def.id));
    }

    const def = adminSectionDefinition(section);
    if (!def) {
        return false;
    }

    // Sekcije koje zahtijevaju superadmin (npr. 101 Korisnici, 104 Dodjele)
    // dostupne su samo superadmin-u (već handlano gore).
    if (def.requiresSuperadmin) {
        return false;
    }

    if (isAdministrator && loose) {
        return true;
    }
    if (hasFullCoarseAdmin(a)) {
        return true;
    }

    return hasGranuleForSection(a, def.id);
}

export function landingCardsForUser(user: { role: string; allowed_modules?: string[] }): readonly AdminSectionDefinition[] {
    // Pod-modul kartice (npr. 110.1/110.2/110.3) ne pojavljuju se na landingu —
    // pristupaju se kroz roditeljsku sekciju (110).
    const topLevel = ADMIN_SECTION_DEFINITIONS.filter((def) => ! def.parentSectionId);
    if (user.role === 'superadministrator') {
        return topLevel;
    }

    return topLevel.filter((def) => userMayAccessAdminSection(user, def.id));
}

/** Pod-sekcije za zadanog roditelja, filtrirano po useru. */
export function childSectionsForUser(user: { role: string; allowed_modules?: string[] }, parentId: AdminSectionNonLanding): readonly AdminSectionDefinition[] {
    return ADMIN_SECTION_DEFINITIONS.filter((def) => def.parentSectionId === parentId && userMayAccessAdminSection(user, def.id));
}

export function parseAdminSectionFromSearch(search: string): AdminSectionId {
    const raw = search.startsWith('?') ? search.slice(1) : search;
    const q = new URLSearchParams(raw).get('admin');
    if (!q || q === 'landing') {
        return 'landing';
    }
    if (q === '121') {
        return '1211';
    }
    const hit = ADMIN_SECTION_DEFINITIONS.find((def) => def.id === q);

    return hit ? hit.id : 'landing';
}

export function syncAdminSectionUrl(section: AdminSectionId): void {
    if (typeof window === 'undefined') {
        return;
    }
    const url = new URL(window.location.href);
    if (section === 'landing') {
        url.searchParams.delete('admin');
    } else {
        url.searchParams.set('admin', section);
    }
    window.history.replaceState({}, '', url.toString());
}
